"use client";

import React, { useEffect, useState } from "react";
import { Box, Fade, Alert, AlertIcon, Text, Flex } from "@chakra-ui/react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useClient } from "../hooks/useClients";
import { ClientData as ServiceClientData, clientService } from "../services/clientService";
import { metricsService, ConversationActionItem } from "../services/metricsService";
import ClientProfile from "../components/clientsprofile/ClientProfile";
import ClientProfileSkeleton from "../components/clientsprofile/ClientProfileSkeleton";

const ClientProfilePage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [apiMeetings, setApiMeetings] = useState<any[]>([]);
  const [conversationActionItems, setConversationActionItems] = useState<ConversationActionItem[]>([]);
  const [isLoadingAdditionalData, setIsLoadingAdditionalData] = useState(false);
  
  // Get name and email from URL search params
  const searchParams = new URLSearchParams(location.search);
  const clientName = searchParams.get('name') || '';
  const clientEmail = searchParams.get('email') || '';
  
  // Keep clientId as string to support alphanumeric IDs like "B_6qPrOygz"
  const clientId = params.id || "1"; // Changed from parseInt to keep as string
  
  // Use the new client hook
  const { client, loading, error, refetch } = useClient({ 
    clientId, 
    enabled: !!clientId 
  });

  // Load additional client data from API with progressive loading
  const loadAdditionalClientData = async (clientId: string, client: ServiceClientData) => {
    if (!clientId) return;
    
    setIsLoadingAdditionalData(true);
    
    try {
      // Start fetching meetings first (most important data)
      const meetingsPromise = clientService.getClientMeetings(clientId);
      
      // Set meetings as soon as they're available (progressive loading)
      meetingsPromise.then(meetingsData => {
        setApiMeetings(meetingsData || []);
        
        // Extract conversation IDs and fetch action items in parallel
        if (meetingsData && meetingsData.length > 0) {
          const conversationIds = meetingsData
            .map((meeting: any) => meeting.conversation_id)
            .filter((id: string) => id && id !== null);
                  
          if (conversationIds.length > 0) {
            // Fetch action items asynchronously (don't block)
            metricsService.getMultipleConversationActionItems(conversationIds)
              .then(actionItems => setConversationActionItems(actionItems))
              .catch(err => console.error("Error fetching action items:", err));
          }
        }
      }).catch(err => console.error("Error fetching meetings:", err));

      // Wait for meetings to complete before marking as done
      await meetingsPromise;
      
    } catch (error) {
      console.error("Error loading additional client profile data:", error);
    } finally {
      setIsLoadingAdditionalData(false);
    }
  };
  
  useEffect(() => {
    // Trigger page fade animation
    setIsLoaded(true);
    
  }, [clientId, clientName, clientEmail, client]);

  useEffect(() => {
    if (client && clientId) {
      // Use a small delay to allow initial render, then fetch additional data
      const timer = setTimeout(() => {
        loadAdditionalClientData(clientId, client);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [client, clientId]);
  
  const handleClose = () => {
    navigate('/clients');
  };

  if (loading) {
    // Show a layout-matching skeleton instead of a blank full-screen spinner so
    // the page structure appears instantly and fills in as data arrives.
    return <ClientProfileSkeleton />;
  }

  if (error) {
    return (
      <Flex justify="center" align="center" height="100vh" direction="column">
        <Alert status="error" variant="subtle" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" height="200px">
          <AlertIcon boxSize="40px" mr={0} />
          <Text fontSize="xl" mt={4} mb={2} fontWeight="bold">
            Error Loading Client Profile
          </Text>
          <Text>{error}</Text>
        </Alert>
      </Flex>
    );
  }

  if (!client) {
    return (
      <Flex justify="center" align="center" height="100vh" direction="column">
        <Text fontSize="lg" color="gray.500">
          Client not found
        </Text>
      </Flex>
    );
  }
  
  return (
    <Fade 
      in={isLoaded} 
      transition={{ enter: { duration: 0.3 } }}
    >
      <Box height="100%" width="100%">
        <ClientProfile 
          clientId={clientId.toString()}
          clientName={client.name || clientName}
          client={client}
          apiMeetings={apiMeetings}
          conversationActionItems={conversationActionItems}
          isLoadingAdditionalData={isLoadingAdditionalData}
        />
        
        {/* {isLoadingAdditionalData && (
          <Flex justify="center" py={4} position="fixed" bottom={4} right={4} bg="white" borderRadius="md" boxShadow="lg" p={3}>
            <CircularProgress isIndeterminate color="purple.500" size="200px" />
            <Text ml={2} fontSize="sm" color="gray.500">Loading additional data...</Text>
          </Flex>
        )} */}
      </Box>
    </Fade>
  );
};

export default ClientProfilePage; 